import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ProductProvider, useProduct } from '@workspace/utils-react';
import type { JSX } from 'react/jsx-runtime';
import { AuthProvider } from 'react-oidc-context';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { ModeProvider } from '@/providers/ModeProvider';
import AppContainerFromTimeTrackPro from '../../resource-scheduler/src/components/AppContainer';
import type { PidgetSettings } from './helpers/PidgetSettings';
import LoginCallback from './LoginCallback';
import { AuthBridgeProvider } from './providers/AuthBridgeProvider';

const clearStaleOidcStorageOnLoad = (() => {
  let hasCleared = false;
  return (authority: string, clientId: string) => {
    if (!hasCleared) {
      const oidcKey = `oidc.user:${authority}:${clientId}`;
      sessionStorage.removeItem(oidcKey);
      hasCleared = true;
    }
  };
})();

const onSigninCallback = (_user: unknown): void => {
  window.history.replaceState({}, document.title, window.location.pathname);
};

export const AppContainer = (): React.ReactElement => {
  const queryClient = new QueryClient();
  const product = useProduct<PidgetSettings>();

  if (product?.settings?.identityUrl) {
    clearStaleOidcStorageOnLoad(
      product.settings.identityUrl,
      product.settings.clientId,
    );

    return (
      <QueryClientProvider client={queryClient}>
        <ProductProvider iframePidget={product}>
          <AuthProvider
            client_id={product.settings.clientId}
            redirect_uri={window.location.origin}
            silent_redirect_uri={`${window.location.origin}/login/callback`}
            scope={product.settings.scope}
            authority={product.settings.identityUrl}
            onSigninCallback={onSigninCallback}
            automaticSilentRenew={true}
            accessTokenExpiringNotificationTimeInSeconds={60}
          >
            <AuthBridgeProvider instanceId={product.instanceId}>
              <ModeProvider>
                <AppContainerFromTimeTrackPro settings={product.settings} liveOnly />
              </ModeProvider>
            </AuthBridgeProvider>
          </AuthProvider>
        </ProductProvider>
      </QueryClientProvider>
    );
  }

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login/callback" element={<LoginCallback />} />
      </Routes>
    </BrowserRouter>
  );
};
